import { extrudeSegment } from "../extrudeSegment";
import { findOpeningRooms } from "../findOpeningRooms";
import { getRoomSearchStepsPt } from "../getRoomSearchStepsPt";
import { getPolygonBounds } from "../getPolygonBounds";
import { measureWallOutline } from "../measureWallOutline";
import { segmentLengthM } from "../segmentLengthM";
import {
  DOOR_RGB,
  OPENING_HALF_WIDTH_PT,
  WALL_RGB,
  WINDOW_RGB,
} from "../constants";
import type { RgbColor } from "utils/hslToRgb";
import type {
  FloorPlanLayoutShape,
  FloorPlanOpeningKind,
} from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/FloorPlanLayoutLayer";
import type { BuildOpeningShapes, BuildWallShapes } from "./types";

const OPENING_RGB_BY_KIND: Record<FloorPlanOpeningKind, RgbColor> = {
  door: DOOR_RGB,
  window: WINDOW_RGB,
};

// One bar per opening; a degenerate segment gives none and is left out.
const buildOpeningShapes: BuildOpeningShapes = ({
  openings,
  openingKind,
  rooms,
  roomSearchStepsPt,
  metersPerPdfPoint,
}) => {
  const shapes: FloorPlanLayoutShape[] = [];

  for (const opening of openings) {
    const bar = extrudeSegment({
      points: opening.points,
      halfWidthPt: OPENING_HALF_WIDTH_PT,
    });
    if (bar.length === 0) continue;
    shapes.push({
      id: `${openingKind}-${opening.id}`,
      rings: [bar],
      rgb: OPENING_RGB_BY_KIND[openingKind],
      label: null,
      tooltip: {
        kind: "opening",
        openingKind,
        widthM: segmentLengthM({ points: opening.points, metersPerPdfPoint }),
        roomNames: findOpeningRooms({
          points: opening.points,
          rooms,
          roomSearchStepsPt,
        }),
      },
    });
  }

  return shapes;
};

// One shape per wall, door and window, so each can be hovered on its own.
export const buildWallShapes: BuildWallShapes = ({
  walls,
  doors,
  windows,
  rooms,
  metersPerPdfPoint,
}) => {
  const wallShapes = walls.map((wall): FloorPlanLayoutShape => {
    const measurements = measureWallOutline({
      points: wall.points,
      metersPerPdfPoint,
    });
    return {
      id: `wall-${wall.id}`,
      rings: [wall.points],
      rgb: WALL_RGB,
      label: null,
      tooltip: {
        kind: "wall",
        lengthM: measurements?.lengthM ?? null,
        thicknessM: measurements?.thicknessM ?? null,
      },
    };
  });

  // Worked out once for the page, shared by every door and window.
  const boundedRooms = rooms.map((room) => ({
    room,
    bounds: getPolygonBounds({ points: room.points }),
  }));
  const roomSearchStepsPt = getRoomSearchStepsPt({ metersPerPdfPoint });

  return [
    ...wallShapes,
    ...buildOpeningShapes({
      openings: doors,
      openingKind: "door",
      rooms: boundedRooms,
      roomSearchStepsPt,
      metersPerPdfPoint,
    }),
    ...buildOpeningShapes({
      openings: windows,
      openingKind: "window",
      rooms: boundedRooms,
      roomSearchStepsPt,
      metersPerPdfPoint,
    }),
  ];
};
